import type {
  IMonitoringResult,
  WhitelistItem,
  WhitelistItems,
} from "@gearbox-protocol/cli-utils";
import type {
  BackendStatus,
  DegenNFTFaucetResponse,
  FaucetClaimResponse,
  ForkStatus,
  ForkTemplateSchema,
  ListedFork,
  PauseResponse,
  PinForkRequest,
  RegisterMidasInvestorResponse,
  RestartResponse,
  ScheduledOptimistInfo,
  Task,
  TaskDefinition,
  TaskDefinitionOverrideSchema,
  TaskDefinitionSchema,
  TaskDefinitionWithArtifacts,
  UnpausedForkStatus,
} from "@gearbox-protocol/deploy-tools-shared";

import type { ExecutionReport } from "@gearbox-protocol/liquidator-v2-config";
import type { NetworkType } from "@gearbox-protocol/sdk/onchain";
import { json_parse, json_stringify } from "@gearbox-protocol/sdk/onchain";
import { jwtDecode } from "jwt-decode";
import type { FileWithPath } from "react-dropzone";
import { kvArrayToRecord, recordToKVArray } from "utils";
import type { Address, Hex, WalletClient } from "viem";
import { createWalletClient, custom, getAddress } from "viem";
import { createSiweMessage } from "viem/siwe";
import { URL_BASE } from "../constants";
import { EVENT_SIGNIN, EVENT_SIGNOUT } from "./constants";
import type {
  TaskDefinitionFormOverrideSchema,
  TaskDefinitionFormSchema,
} from "./schemas";
import type {
  AccessTokenPayload,
  ApiError,
  BackendSyncStatus,
  FileTree,
  User,
  VersionResponse,
} from "./types";

export interface FaucetClaimParams {
  /** Human-readable USD amount, backend defaults it when omitted */
  usdAmount?: string;
  /** Also greenlist the recipient on Midas gateways */
  midas?: boolean;
}

class Api extends EventTarget {
  #base: string;
  #version?: string | undefined;
  #user?: User | null = null;
  #accessToken?: string | null = null;

  constructor() {
    super();
    this.#base = `${URL_BASE}/api`;
  }

  async signIn(): Promise<void> {
    await this.#signIn();
  }

  async refresh(): Promise<void> {
    await this.#refresh();
  }

  async signOut(): Promise<void> {
    await fetch(`${URL_BASE}/auth/signout`, { credentials: "include" });
    this.#updateUser("");
  }

  async version(): Promise<string> {
    if (!this.#version) {
      const raw = await fetch(`${this.#base}/version`);
      const { version }: VersionResponse = await raw.json();
      this.#version = version;
    }
    return this.#version;
  }

  async forks(): Promise<ListedFork[]> {
    const raw = await fetch(`${this.#base}/forks`);
    const f = await raw.json();
    return f;
  }

  async status(executionId?: string): Promise<ForkStatus> {
    if (!executionId) {
      throw new Error("execution id not specified");
    }
    const raw = await fetch(`${this.#base}/forks/${executionId}`);
    let body: unknown;
    try {
      body = await raw.json();
    } catch {}
    if (!raw.ok) {
      if (body && typeof body === "object" && "error" in body) {
        throw new Error((body as ApiError).error);
      }
      throw new Error(`failed to get fork ${executionId}: ${raw.status}`);
    }
    return body as ForkStatus;
  }

  public artifactURL(executionId: string, file: string): string {
    return `${this.#base}/forks/${executionId}/${file}`;
  }

  async artifacts(executionId?: string): Promise<FileTree> {
    // TODO: handle errors
    if (!executionId) {
      throw new Error("execution id not specified");
    }
    const raw = await fetch(`${this.#base}/forks/${executionId}/artifacts`);
    const files: string[] = await raw.json();
    // export interface FileTree {
    //   dir: string;
    //   content: Array<string | FileTree>;
    const root: FileTree = { dir: "/", content: [] }; // Changed dir from "/" to ""

    files.forEach(file => {
      const parts = file.split("/").filter(part => part); // Filter out empty strings to avoid creating an unnecessary level for the root
      let current = root;

      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          // It's a file
          current.content.push(part);
        } else {
          // It's a directory
          let subdir = current.content.find(
            c => typeof c !== "string" && c.dir === part,
          );
          if (!subdir) {
            subdir = { dir: part, content: [] };
            current.content.push(subdir);
          }
          current = subdir as FileTree;
        }
      });
    });

    return root;
  }

  /**
   * Fetches latest synced block from the fork's backend sidecar
   * via anvil-manager backend proxy.
   * When the sidecar is not active, the proxy responds with BackendStatus json
   * and this method throws with its state/error.
   */
  async backendSyncStatus(
    executionId: string,
    chainId: number,
  ): Promise<BackendSyncStatus> {
    const resp = await fetch(
      `${this.#base}/forks/${executionId}/backend/v1/sync?chainId=${chainId}`,
    );
    const data = await resp.json();
    if (!resp.ok) {
      const st = data as BackendStatus;
      throw new Error(st.error || `backend is ${st.state || "unavailable"}`);
    }
    return data as BackendSyncStatus;
  }

  async requestEth(
    executionId: string,
    account: string,
    amount?: number,
  ): Promise<Hex> {
    let url = `${this.#base}/forks/${executionId}/eth-faucet?to=${account}`;
    if (amount) {
      url += `&amount=${amount}`;
    }
    const resp = await fetch(url);
    const receipt = await resp.json();
    return receipt.transactionHash;
  }

  async requestDegenNft(
    executionId: string,
    account: string,
    count: number,
  ): Promise<DegenNFTFaucetResponse> {
    const resp = await fetch(
      `${this.#base}/forks/${executionId}/degen-faucet?to=${account}&count=${count}`,
    );
    return resp.json();
  }

  async claimFromFaucet(
    executionId: string,
    account: string,
    params: FaucetClaimParams = {},
  ): Promise<FaucetClaimResponse> {
    const { usdAmount, midas } = params;
    let url = `${this.#base}/forks/${executionId}/faucet?to=${account}&force=true`;
    if (usdAmount?.trim()) {
      url += `&usd=${encodeURIComponent(usdAmount.trim())}`;
    }
    if (midas) {
      url += "&midas=true";
    }
    const resp = await fetch(url);
    const body = await resp.json();
    if (!resp.ok) {
      throw new Error(
        (body as ApiError).error ?? `faucet claim failed: ${resp.status}`,
      );
    }
    return body as FaucetClaimResponse;
  }

  async registerMidasInvestor(
    executionId: string,
    investor: Address,
    token?: Address,
  ): Promise<RegisterMidasInvestorResponse> {
    let url = `${this.#base}/forks/${executionId}/register-midas-investor?investor=${encodeURIComponent(investor)}`;
    if (token) {
      url += `&token=${encodeURIComponent(token)}`;
    }
    const resp = await fetch(url);
    const body = await resp.json();
    if (!resp.ok) {
      throw new Error(
        (body as ApiError).error ??
          `register midas investor failed: ${resp.status}`,
      );
    }
    return body as RegisterMidasInvestorResponse;
  }

  async makePersistent(req: PinForkRequest): Promise<void> {
    await this.#signAndPost("/forks/persistent", req);
  }

  async removePersistent(alias: string): Promise<void> {
    await this.#signAndDelete(`/forks/persistent/${alias}`);
  }

  /**
   * Restarts execution, returns id or alias for new execution
   * @param executionId
   */
  async restart(
    executionId: string,
    force?: boolean,
  ): Promise<RestartResponse> {
    const query = force ? "?force=true" : "";
    return this.#signAndPost<RestartResponse>(
      `/forks/${executionId}/restart${query}`,
    );
  }

  /**
   * Restarts execution, returns id or alias for new execution
   * @param executionId
   */
  async setPaused(alias: string, paused: boolean): Promise<PauseResponse> {
    return this.#signAndPost<PauseResponse>(
      `/forks/persistent/${alias}/pause`,
      { paused },
    );
  }

  async kill(executionId: string): Promise<void> {
    await this.#signAndDelete(`/forks/${executionId}`);
  }

  async createFork(
    json: ForkTemplateSchema,
    files: FileWithPath[],
  ): Promise<UnpausedForkStatus> {
    const payload = this.#upsertForkPayload(json, files);
    return this.#signAndPost<UnpausedForkStatus>(`/forks`, payload);
  }

  async updateFork(
    alias: string,
    json: ForkTemplateSchema,
    files: FileWithPath[],
  ): Promise<RestartResponse> {
    // Always multipart so an empty file list is an explicit full-set replace
    // (delete all), not a bare JSON body that omits files.
    return this.#signAndPut<RestartResponse>(
      `/forks/${alias}`,
      this.#forkFormData(json, files),
    );
  }

  #upsertForkPayload(json: ForkTemplateSchema, files: FileWithPath[]): object {
    if (files.length) {
      return this.#forkFormData(json, files);
    }

    return json;
  }

  #forkFormData(json: ForkTemplateSchema, files: FileWithPath[]): FormData {
    const formData = new FormData();
    formData.append("json", JSON.stringify(json));
    files.forEach(file => {
      formData.append("file", file, file.name);
    });
    return formData;
  }

  async optimistReport(executionId?: string): Promise<ExecutionReport> {
    if (!executionId) {
      throw new Error("execution id not specified");
    }
    return this.#fetchJsonArtifact(executionId, "optimist/output.json");
  }

  async listScheduledOptimists(): Promise<ScheduledOptimistInfo[]> {
    const resp = await fetch(`${this.#base}/scheduled-optimist`);
    return resp.json();
  }

  async optimistTrace(executionId?: string, traceId?: string): Promise<string> {
    if (!executionId) {
      throw new Error("execution id not specified");
    }
    if (!traceId) {
      throw new Error("trace id not specified");
    }
    const resp = await fetch(
      this.artifactURL(executionId, `optimist/${traceId}`),
    );
    return resp.text();
  }

  async startTask(
    executionId: string,
    def: TaskDefinitionFormOverrideSchema,
  ): Promise<void> {
    const payload = this.#prepareTaskDefinition(def);
    return this.#signAndPost(`/forks/${executionId}/tasks`, payload);
  }

  taskArtifactURL({ executionId, outputDir }: Task, file: string): string {
    return this.artifactURL(executionId, `${outputDir}/${file}`);
  }

  async fetchTaskArtifact<T = unknown>(task: Task, file: string): Promise<T> {
    const artifact = this.taskArtifactURL(task, file);
    const resp = await fetch(artifact);
    if (resp.status === 404) {
      throw new Error(`artifact not found: ${artifact}`);
    }
    const raw = await resp.text();
    if (file.toLowerCase().endsWith(".json")) {
      return json_parse(raw) as T;
    }
    return raw as T;
  }

  async createTaskDefinition(
    def: TaskDefinitionFormSchema,
  ): Promise<TaskDefinitionWithArtifacts> {
    const payload = this.#prepareTaskDefinition(def);
    return this.#signAndPost("/tasks/definitions", payload);
  }

  async updateTaskDefinition(
    def: TaskDefinitionFormSchema,
  ): Promise<TaskDefinitionWithArtifacts> {
    const payload = this.#prepareTaskDefinition(def);
    return this.#signAndPut(`/tasks/definitions/${def.id}`, payload);
  }

  async deleteTaskDefinition(id: string): Promise<void> {
    return this.#signAndDelete(`/tasks/definitions/${id}`);
  }

  async listTaskDefinitions(): Promise<TaskDefinition[]> {
    return this.#signAndGet("/tasks/definitions");
  }

  async listWhitelist(category: string): Promise<WhitelistItem[]> {
    const resp = await fetch(`${this.#base}/whitelist/${category}`);
    return resp.json();
  }

  async addToWhitelist(category: string, data: WhitelistItems): Promise<void> {
    return this.#signAndPost(`/whitelist`, {
      ...data,
      category,
    });
  }

  async deleteWhitelist(
    category: string,
    items: { network: NetworkType; address: Address }[],
  ): Promise<void> {
    if (items.length === 0) {
      return;
    }
    return this.#signAndDelete(
      `/whitelist`,
      items.map(i => ({
        network: i.network,
        category,
        address: i.address,
      })),
    );
  }

  async getTaskDefinition(id: string): Promise<TaskDefinitionFormSchema> {
    const resp = await this.#signAndGet<TaskDefinitionWithArtifacts>(
      `/tasks/definitions/${id}`,
    );
    const { artifacts, env, secrets, ...rest } = resp;
    const result: TaskDefinitionFormSchema = {
      ...rest,
      id: rest.id ?? id,
      env: recordToKVArray(env),
      secrets: recordToKVArray(secrets),
    };
    if (artifacts?.length) {
      result.artifacts = await Promise.all(
        artifacts.map(async f => {
          const raw = await this.#getRaw(
            `/tasks/definitions/${id}/artifacts/${f}`,
          );
          return new File([raw], f, { type: mimeForFilename(f) });
        }),
      );
    }
    return result;
  }

  async getMonitoringData(): Promise<IMonitoringResult> {
    await this.#refresh();
    const resp = await fetch(
      "https://b5mcxmmynrot7adlatwmoshpka0tfzsl.lambda-url.eu-west-1.on.aws/",
      {
        headers: {
          Authorization: `Bearer ${this.#accessToken}`,
        },
      },
    );
    const raw = await resp.text();
    const data = json_parse(raw) as IMonitoringResult | { error: "string" };
    if ("error" in data) {
      throw new Error(data.error);
    }
    return data;
  }

  #prepareTaskDefinition(
    def: TaskDefinitionFormSchema | TaskDefinitionFormOverrideSchema,
  ) {
    const { artifacts, secrets, env, ...rest } = def;
    const payload: TaskDefinitionSchema | TaskDefinitionOverrideSchema = {
      ...rest,
      env: kvArrayToRecord(env),
      secrets: kvArrayToRecord(secrets),
    };
    if (artifacts?.length) {
      const formData = new FormData();
      formData.append("json", JSON.stringify(payload));
      artifacts.forEach(file => {
        formData.append("file", file, file.name);
      });
      return formData;
    }
    return payload;
  }

  async #fetchJsonArtifact<T>(executionId: string, file: string): Promise<T> {
    const url = this.artifactURL(executionId, file);
    const resp = await fetch(url);
    if (resp.status < 200 || resp.status >= 400) {
      throw new Error(`failed to fetch ${url}: ${resp.status}`);
    }
    const raw = await resp.text();
    return json_parse(raw);
  }

  async #getWallet(): Promise<WalletClient> {
    if (!window.ethereum) {
      throw new Error("window.ethereum not present");
    }
    const [account] = await window.ethereum.request({
      method: "eth_requestAccounts",
    });
    try {
      return createWalletClient({
        account,
        transport: custom(window.ethereum),
      });
    } catch (e) {
      throw new Error(`cannot wallet client: ${e}`);
    }
  }

  async #signAndPost<T = unknown>(
    path: string,
    data?: object | undefined,
  ): Promise<T> {
    return this.#send<T>(path, "POST", data, true);
  }

  async #signAndPut<T = unknown>(
    path: string,
    data?: object | undefined,
  ): Promise<T> {
    return this.#send<T>(path, "PUT", data, true);
  }

  async #signAndDelete<T = unknown>(
    path: string,
    data?: object | undefined,
  ): Promise<T> {
    return this.#send(path, "DELETE", data, true);
  }

  async #signAndGet<T = unknown>(path: string): Promise<T> {
    return this.#send(path, "GET", undefined, true, true);
  }

  async #send<T = unknown>(
    path: string,
    method: string,
    data: object | undefined,
    requireAuth: boolean,
    noSignIn = false,
  ): Promise<T> {
    if (requireAuth && !this.#user && !noSignIn) {
      await this.#signIn();
    }
    const headers: Record<string, string> = {};
    let body: BodyInit | null | undefined;
    if (data) {
      if (data instanceof FormData) {
        body = data;
      } else {
        body = json_stringify(data);
        headers["Content-Type"] = "application/json";
      }
    }
    const performFetch = async () =>
      fetch(`${this.#base}${path}`, {
        method,
        body,
        credentials: requireAuth ? "include" : undefined,
        headers,
      });
    let resp = await performFetch();
    if (
      requireAuth &&
      (resp.status === 401 || resp.status === 403) &&
      this.#user
    ) {
      await this.#refresh();
      resp = await performFetch();
    }

    let respData: any;
    try {
      respData = await resp.text();
      if (resp.headers.get("Content-Type")?.includes("application/json")) {
        respData = json_parse(respData);
      }
    } catch {}
    if (resp.status < 200 || resp.status >= 400) {
      if (respData && typeof respData === "object" && "error" in respData) {
        throw new Error((respData as ApiError).error);
      }
      throw new Error(`failed to ${method} ${path}: ${resp.status}`);
    }

    return respData;
  }

  async #getRaw(path: string, requireAuth = true): Promise<string> {
    let resp = await fetch(`${this.#base}${path}`, {
      credentials: "include",
    });
    if (requireAuth && resp.status === 401 && this.#user) {
      await this.#refresh();
      resp = await fetch(`${this.#base}${path}`, {
        credentials: "include",
      });
    }

    if (resp.status < 200 || resp.status >= 400) {
      throw new Error(`failed to get ${path}: ${resp.status}`);
    }

    return resp.text();
  }

  async #signIn(): Promise<void> {
    const wallet = await this.#getWallet();
    const nonceResp = await fetch(`${URL_BASE}/auth/nonce`, {
      credentials: "include",
    });
    const nonceData = await nonceResp.json();
    if (nonceResp.status !== 200) {
      throw new Error(`failed to get nonce: ${nonceData.error}`);
    }
    const { nonce } = nonceData;
    if (!wallet.account) {
      throw new Error("wallet account not found");
    }
    const message = createSiweMessage({
      domain: window.location.host,
      address: getAddress(wallet.account.address),
      statement: "sign in",
      uri: window.location.origin,
      version: "1",
      chainId: 1,
      nonce,
    });
    const signature = await wallet.signMessage({
      account: wallet.account,
      message,
    });
    const resp = await fetch(`${URL_BASE}/auth/signin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        signature,
        message,
      }),
    });
    const data = await resp.json();
    if ("error" in data) {
      throw new Error(`failed to sign in: ${data.error}`);
    }
    this.#updateUser(data.access_token);
  }

  #updateUser(accessToken: string): void {
    if (!accessToken) {
      console.info("signout");
      this.#user = null;
      this.#accessToken = null;
      this.dispatchEvent(new CustomEvent(EVENT_SIGNOUT));
      return;
    }
    try {
      // read username and address from jwt cookie
      const { username, address } = jwtDecode<AccessTokenPayload>(accessToken);
      console.info(`signed in as ${username} (${address})`);
      this.#user = { username, address };
      this.#accessToken = accessToken;
      this.dispatchEvent(
        new CustomEvent(EVENT_SIGNIN, {
          detail: { username, address } as User,
        }),
      );
    } catch {
      console.info("signout");
      this.#user = null;
      this.#accessToken = null;
      this.dispatchEvent(new CustomEvent(EVENT_SIGNOUT));
    }
  }

  async #refresh(): Promise<void> {
    const resp = await fetch(`${URL_BASE}/auth/refresh`, {
      credentials: "include",
    });
    const data = await resp.json();
    if ("error" in data) {
      throw new Error(
        `failed to refresh access token: ${(data as ApiError).error}`,
      );
    }
    this.#updateUser(data.access_token);
  }
}

function mimeForFilename(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "json":
      return "application/json";
    case "yaml":
    case "yml":
      return "application/yaml";
    default:
      return "text/plain";
  }
}

export const api = new Api();
